import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

// Toolkit
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { assessmentUpdate } from "sj-ui-toolkit/src/client";
import { FullScreenLoader } from "sj-ui-toolkit/src/components";
import { buildRoute } from "sj-ui-toolkit/src/constants/routes";
import { useTrackTime } from "sj-ui-toolkit/src/hooks/useTrackTime";
import { exitScormLesson, setScormPassed } from "sj-ui-toolkit/src/utils/rxd";

// Context providers
import { useLearnContext } from "../../context/LearnContext";

// Hooks
import { useLessonLoader } from "../../hooks/useLessonLoader";
import { usePathCompletion } from "../../hooks/usePathCompletion";

// Sub-components
import AppLabLesson from "./AppLabLesson/AppLabLesson.jsx";
import LessonCoverPage from "./LessonCoverPage.jsx";
import { PathCompletionPage } from "./PathCompletion/PathCompletionPage.jsx";
import SandboxLesson from "./SandboxLesson";
import VideoLesson from "./VideoLesson";

// Main component
export default function LessonPage() {
  const { lessonKey } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isScorm, isGuest } = useLearnContext();

  // Use the lesson loader hook
  const { lessonDetails, setLessonDetails } = useLessonLoader(lessonKey);
  const hasNextLesson =
    searchParams.get("inPath") === "true" && !!lessonDetails?.nextLesson?.lessonKey;

  // Register this page's breadcrumb — trail is auto-accumulated by the provider
  useBreadcrumb(
    lessonDetails ? { label: lessonDetails.title, route: `/lesson/${lessonKey}` } : null
  );

  // Track time spent on the lesson
  useTrackTime({ lessonId: lessonDetails?.id });

  // Subscribe to PathCompletedChannel — silently stores data until lesson finishes
  const { isPathCompleted, completedPath } = usePathCompletion();

  // Track whether to show the path completion screen (after survey, before navigation)
  const [showPathCompletion, setShowPathCompletion] = useState(false);

  // Define handlers for user actions
  const submitKnowledgeRating = async (rating, isExiting = false) => {
    const isAfterPage = lessonDetails.showAfterSurvey;

    if (rating) {
      const params = {};
      if (isAfterPage) {
        params.after_rating = parseInt(rating, 10);
      } else {
        params.before_rating = parseInt(rating, 10);
      }

      try {
        await assessmentUpdate({
          id: lessonDetails.assessment.id,
          paramsToUpdate: params,
          useToast: false,
        });
      } catch (error) {
        console.error("Error submitting knowledge rating:", error);
      }
    }

    // Proceed based on whether it was before or after survey
    if (isAfterPage) {
      // If a path was completed, show the path completion screen before navigating
      if (isPathCompleted && completedPath) {
        setShowPathCompletion(true);
        return;
      }

      if (isExiting) {
        navigate("/");
        return;
      }
      // Navigate to the next lesson if we're in a path context and nextLesson exists
      if (hasNextLesson) {
        navigate(
          buildRoute(
            "Learn.routes.lesson.route",
            {
              lessonKey: lessonDetails.nextLesson?.lessonKey,
            },
            { inPath: true }
          )
        );
      } else {
        navigate("/");
      }
    } else {
      // Hide the before survey and start the lesson
      setLessonDetails({ ...lessonDetails, showBeforeSurvey: false });
    }
  };

  const onLessonFinish = () => {
    if (!isGuest && !isScorm) {
      setLessonDetails({ ...lessonDetails, showAfterSurvey: true });
    } else {
      // Set SCORM as passed and exit the lesson for guest users and SCORM content since they won't see the after lesson survey
      setScormPassed();
      exitScormLesson();
    }
  };

  // Return the full screen loader until we have lesson details
  if (!lessonDetails) {
    return (
      <div className="h-full">
        <FullScreenLoader />
      </div>
    );
  }

  // Show path completion screen after the survey if a path was completed
  if (showPathCompletion && completedPath) {
    return <PathCompletionPage completedPath={completedPath} />;
  }

  // Render the After Lesson Feedback page if needed
  if (lessonDetails.showAfterSurvey) {
    return (
      <LessonCoverPage
        headerText="Lesson complete!"
        description="Let us know how you did."
        lessonType={lessonDetails.type}
        title={lessonDetails.title}
        level={lessonDetails.level}
        duration={lessonDetails.duration}
        isAfterLessonSurvey={true}
        onSubmit={submitKnowledgeRating}
        hasNextLesson={hasNextLesson}
      />
    );
  }

  // Render the Getting Started screen/survey if needed
  if (lessonDetails.showBeforeSurvey && !isGuest && !isScorm) {
    return (
      <LessonCoverPage
        headerText="Let's get started."
        description={lessonDetails.description}
        lessonType={lessonDetails.type}
        title={lessonDetails.title}
        difficulty={lessonDetails.difficulty}
        duration={lessonDetails.duration}
        isAfterLessonSurvey={false}
        onSubmit={submitKnowledgeRating}
      />
    );
  }

  // Render the appropriate lesson type
  if (lessonDetails.isBreakFix) {
    if (lessonDetails.lessonType === "AppLab") {
      return <AppLabLesson lessonDetails={lessonDetails} onLessonFinish={onLessonFinish} />;
    } else {
      return <SandboxLesson lessonDetails={lessonDetails} onLessonFinish={onLessonFinish} />;
    }
  } else {
    return <VideoLesson lessonDetails={lessonDetails} onLessonFinish={onLessonFinish} />;
  }
}
